import type { IDesign, IImage } from "@designcombo/types";
import type { WaterMarkSchemaType } from "@repo/types";
import { DefaultWaterMarkOptions } from "@repo/types/myTypes";
import { devLog } from "../logger";
import { generateId } from "../trackId";
import { createImageTrackItem, getImageDimensionsForWidth, getScaleForTargetSize } from "./image-mapper";
import type { ImageDetailsWithLayout } from "./image-mapper";

const DEFAULT_TRANSFORM = "scale(1)";


// Utility function to check if a URL is the default watermark URL
export function isDefaultWatermarkUrl(url: string | null | undefined): boolean {
	return url === DefaultWaterMarkOptions.waterMarkUrl;
}

// Function to detect if a design has default watermarks
export function hasDefaultWatermark(design: IDesign): boolean {
	return design.trackItemsMap && Object.values(design.trackItemsMap).some(
		(item) => item.type === "image" && 
		item.details?.src && isDefaultWatermarkUrl(item.details.src)
	);
}

// Function to remove default watermarks from a design
export function removeDefaultWatermarks(design: IDesign): IDesign {
	// Check if design has any default watermarks to remove
	if (!hasDefaultWatermark(design)) {
		return design;
	}

	// Create a copy of the design to avoid mutating the original
	const processedDesign = { ...design };
	
	// Ensure required properties exist
	if (!processedDesign.trackItemsMap) {
		processedDesign.trackItemsMap = {};
	}
	if (!processedDesign.trackItemIds) {
		processedDesign.trackItemIds = [];
	}
	if (!processedDesign.tracks) {
		processedDesign.tracks = [];
	}

	// Find and remove all default watermarks
	const watermarkIdsToRemove: string[] = [];
	Object.entries(processedDesign.trackItemsMap).forEach(([id, item]) => {
		if (item.type === "image" && item.details?.src && isDefaultWatermarkUrl(item.details.src)) {
			watermarkIdsToRemove.push(id);
		}
	});

	// Remove watermarks from trackItemsMap and trackItemIds
	watermarkIdsToRemove.forEach(id => {
		delete processedDesign.trackItemsMap[id];
		const index = processedDesign.trackItemIds.indexOf(id);
		if (index > -1) {
			processedDesign.trackItemIds.splice(index, 1);
		}
	});

	// Remove watermark IDs from tracks
	processedDesign.tracks.forEach(track => {
		if (track.type === "image") {
			track.items = track.items.filter(id => !watermarkIdsToRemove.includes(id));
		}
	});

	// Remove empty image tracks only
	processedDesign.tracks = processedDesign.tracks.filter(track => 
		!(track.type === "image" && track.items.length === 0)
	);

	devLog.log("Removed default watermarks:", watermarkIdsToRemove);
	
	return processedDesign;
}


export async function createWatermarkLayerPayload(
	videoDuration: number,
	_resolution: {
		width: number;
		height: number;
	},
	watermarkOptions: WaterMarkSchemaType,
): Promise<IImage> {
	devLog.log("🖼️ [WATERMARK] createWatermarkLayerPayload called with:", {
		videoDuration,
		resolution: _resolution,
		watermarkOptions: {
			waterMarkUrl: watermarkOptions.waterMarkUrl,
			width: watermarkOptions.width,
			height: watermarkOptions.height,
			top: watermarkOptions.top,
			left: watermarkOptions.left,
			opacity: watermarkOptions.opacity,
			blendMode: watermarkOptions.blendMode,
		},
	});
	
    const dimensions = await getImageDimensionsForWidth(
		watermarkOptions.waterMarkUrl,
		watermarkOptions.width
	);
	
	devLog.log("🖼️ [WATERMARK] Image dimensions calculated:", {
		originalWidth: dimensions.originalWidth,
		originalHeight: dimensions.originalHeight,
		width: dimensions.width,
		height: dimensions.height,
	});
	
	const targetWidth = Number.parseFloat(watermarkOptions.width || "0") || dimensions.width;
	const targetHeight = Number.parseFloat(watermarkOptions.height || "0") || dimensions.height;
	const watermarkWidth = dimensions.originalWidth;
	const watermarkHeight = dimensions.originalHeight;
	
	devLog.log("🖼️ [WATERMARK] Size calculations:", {
		targetWidth,
		targetHeight,
		watermarkWidth,
		watermarkHeight,
		widthFromOptions: watermarkOptions.width,
		heightFromOptions: watermarkOptions.height,
	});
	
	let transform = DEFAULT_TRANSFORM;
	if (
		watermarkWidth && watermarkHeight &&
		targetWidth !== watermarkWidth &&
		targetHeight !== watermarkHeight
	) {
		const _transform = getScaleForTargetSize({
			width: watermarkWidth,
			height: watermarkHeight,
			targetWidth: Number.parseFloat(watermarkOptions.width || "0") || undefined,
			targetHeight: Number.parseFloat(watermarkOptions.height || "0") || undefined,
		});
		transform = `scale(${_transform})`;
		devLog.log("🖼️ [WATERMARK] Transform calculated:", {
			scale: _transform,
			transform,
			reason: "targetWidth !== watermarkWidth || targetHeight !== watermarkHeight",
		});
	} else {
		devLog.log("🖼️ [WATERMARK] Using default transform:", transform, {
			reason: watermarkWidth && watermarkHeight && targetWidth === watermarkWidth && targetHeight === watermarkHeight
				? "sizes match, no scaling needed"
				: "missing dimensions",
		});
	}
	
	let top: string;
	/** Centering offset minus user "inset from top" (px). Negated once for final CSS `top`. */
	function computeWatermarkTopPx(topValuePx: number): string {
		const rawTop =
			watermarkHeight / 2 -
			(dimensions.height! / 2) -
			topValuePx;
		// Avoid `-${negative}px` → invalid `--20px` when image height matches scaled height.
		return `${-rawTop}px`;
	}

	if (watermarkOptions.top === undefined || watermarkOptions.top === null) {
		// Default: 30px from top
		const topValue = 30;
		top = computeWatermarkTopPx(topValue);
		devLog.log("🖼️ [WATERMARK] Top position (default):", {
			topFromOptions: watermarkOptions.top,
			topValueParsed: topValue,
			watermarkHeight,
			dimensionsHeight: dimensions.height,
			result: top,
		});
	} else {
		const topValue = Number.parseInt(String(watermarkOptions.top), 10);
		top = Number.isFinite(topValue)
			? computeWatermarkTopPx(topValue)
			: computeWatermarkTopPx(30);
		devLog.log("🖼️ [WATERMARK] Top position calculation:", {
			topFromOptions: watermarkOptions.top,
			topValueParsed: topValue,
			watermarkHeight,
			dimensionsHeight: dimensions.height,
			result: top,
		});
	}

	let left: string;
	// Check if left is null, undefined, or a CSS calc expression (which means use default right-aligned)
	const isLeftNullOrCalc = watermarkOptions.left === undefined || 
		watermarkOptions.left === null || 
		(typeof watermarkOptions.left === "string" && watermarkOptions.left.trim().startsWith("calc("));
	
	if (isLeftNullOrCalc) {
		// Default: right-aligned - position right edge 30px from right
		// The visual element's right edge should be 30px from the right
		// Visual center should be at: 100% - 30px - (visualWidth/2)
		// DOM center should be at the same position (transform-origin is center)
		// left = 100% - 30px - (visualWidth/2) - (DOMWidth/2)
		// = 100% - 30px - (visualWidth + DOMWidth)/2
		const visualWidth = dimensions.width!;
		left = `calc(100% - ${(visualWidth + watermarkWidth)/2 + 30}px)`;
		devLog.log("🖼️ [WATERMARK] Left position (default/right-aligned):", {
			leftFromOptions: watermarkOptions.left,
			isNull: watermarkOptions.left === null,
			isUndefined: watermarkOptions.left === undefined,
			isCalc: typeof watermarkOptions.left === "string" && watermarkOptions.left.trim().startsWith("calc("),
			visualWidth,
			watermarkWidth,
			calculation: `calc(100% - ${(visualWidth + watermarkWidth)/2 + 30}px)`,
			result: left,
		});
	} else {
		// Extract numeric value from the string (remove "px" suffix if present)
		const leftString = (watermarkOptions.left || "").replace(/px\s*$/i, "").trim();
		const leftValue = Number.parseInt(leftString);
		
		if (Number.isNaN(leftValue)) {
			devLog.warn("🖼️ [WATERMARK] Invalid left value, using default right-aligned:", watermarkOptions.left);
			// Fallback to default right-aligned if parsing fails
			const visualWidth = dimensions.width!;
			left = `calc(100% - ${(visualWidth + watermarkWidth)/2 + 30}px)`;
		} else {
			// Position the left edge of the visual element at leftValue pixels from left
			// Visual center should be at: leftValue + (visualWidth/2)
			// DOM center should be at the same position
			// left = leftValue + (visualWidth/2) - (DOMWidth/2)
			const visualWidth = dimensions.width!;
			left = `${leftValue + (visualWidth/2) - (watermarkWidth/2)}px`;
			devLog.log("🖼️ [WATERMARK] Left position calculation:", {
				leftFromOptions: watermarkOptions.left,
				leftString,
				leftValueParsed: leftValue,
				watermarkWidth,
				visualWidth: dimensions.width,
				calculation: `${leftValue} + (${visualWidth}/2) - (${watermarkWidth}/2) = ${leftValue + (visualWidth/2) - (watermarkWidth/2)}`,
				result: left,
			});
		}
	}

	const opacity = Number.parseFloat(watermarkOptions.opacity || "1") * 100;
	const blendMode = watermarkOptions.blendMode || "normal";
	
	devLog.log("🖼️ [WATERMARK] Opacity and blend mode:", {
		opacityFromOptions: watermarkOptions.opacity,
		opacityCalculated: opacity,
		blendMode,
	});

	const overrides: Partial<ImageDetailsWithLayout> = {
		blendMode,
		transform,
		border: "none",
		borderRadius: 0,
		borderWidth: 0,
		borderColor: "#000000",
		blur: 0,
		brightness: 100,
		flipX: false,
		flipY: false,
		rotate: "0deg",
		visibility: "visible",
	};

	devLog.log("🖼️ [WATERMARK] Creating image track item with:", {
		src: watermarkOptions.waterMarkUrl,
		width: watermarkWidth,
		height: watermarkHeight,
		opacity,
		top,
		left,
		zIndex: 9999,
		transform: overrides.transform,
		blendMode: overrides.blendMode,
		display: {
			from: 0,
			to: videoDuration,
		},
		duration: videoDuration,
	});
	
	const watermarkPayload = createImageTrackItem({
		src: watermarkOptions.waterMarkUrl,
		width: watermarkWidth,
		height: watermarkHeight,
		opacity,
		top,
		left,
		zIndex: 9999,
		overrides,
		display: {
			from: 0,
			to: videoDuration,
		},
		duration: videoDuration,
		name: "Watermark",
		metadata: {
			previewUrl: watermarkOptions.waterMarkUrl,
		},
		id: `logo-swiftia-default-${generateId(10)}`,
	});

	devLog.log("🖼️ [WATERMARK] Created watermark layer payload:", {
		id: watermarkPayload.id,
		name: watermarkPayload.name,
		src: (watermarkPayload.details as any)?.src,
		width: (watermarkPayload.details as any)?.width,
		height: (watermarkPayload.details as any)?.height,
		top: (watermarkPayload.details as any)?.top,
		left: (watermarkPayload.details as any)?.left,
		opacity: (watermarkPayload.details as any)?.opacity,
		blendMode: (watermarkPayload.details as any)?.blendMode,
		transform: (watermarkPayload.details as any)?.transform,
		zIndex: (watermarkPayload.details as any)?.zIndex,
		display: watermarkPayload.display,
		duration: watermarkPayload.duration,
	});

	return watermarkPayload;
}
